import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ROUTES } from "@/app/routes";
import { ContinueWithGoogleButton } from "@/features/identity/components/continue-with-google-button";
import {
  useCompleteSignup,
  useRequestSignupToken,
  useVerifySignupToken,
} from "@/features/identity/hooks/use-signup-flow";
import { PASSWORD_MIN_LENGTH } from "@/features/identity/schemas/auth.schema";
import {
  trackSignupStarted,
  trackSignupVerificationRequested,
  trackSignupVerified,
} from "@/shared/analytics/registration-analytics";
import { pendingConsentReturnTo } from "@/shared/auth/oauth-return-to";
import { LogoLockup } from "@/shared/components/brand/logo-mark";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

const route = getRouteApi(ROUTES.auth.signup.id);

/**
 * Signup landing, `/signup`. Collects name, email, and a password. On submit
 * we issue a token (carrying the hashed password) and show the "check your
 * email" panel with an OTP entry input. The account, with its password, is
 * created once the code is verified.
 */
export function SignupRoute() {
  const navigate = useNavigate();
  const search = route.useSearch();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(search.email ?? "");
  const [password, setPassword] = useState("");
  const [planKey, setPlanKey] = useState<"trial" | "elevate">("elevate");
  const [stage, setStage] = useState<"enter-email" | "check-email">("enter-email");
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const requestToken = useRequestSignupToken();
  const verify = useVerifySignupToken();
  const complete = useCompleteSignup("email");

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!firstName || !lastName || !email) return;
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Use a password of at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }
    try {
      trackSignupStarted("email");
      await requestToken.mutateAsync({ firstName, lastName, email, password, planKey });
      trackSignupVerificationRequested();
      setStage("check-email");
    } catch {
      setError("Could not send the verification code. Try again.");
    }
  }

  async function onSubmitOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (otpCode.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    try {
      const result = await verify.mutateAsync({ email, otpCode });
      trackSignupVerified("email");
      await complete.mutateAsync({ token: result.token, planKey });
      const consentReturn = pendingConsentReturnTo();
      if (consentReturn) {
        // reason: full reload so the consent page boots with the new session.
        window.location.assign(consentReturn);
        return;
      }
      await navigate({ to: "/", search: { firstRun: 1 } });
    } catch {
      setError("That code is invalid or expired. Request a new one.");
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-8 px-6 py-12">
      <div>
        <LogoLockup className="w-fit" />
        <h1 className="font-display mt-4 text-3xl font-semibold tracking-tighter">
          Create your account
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          Pulsewise is invite-only. Share your details and choose a password, we'll email a 6-digit
          code to confirm it's you.
        </p>
      </div>

      {stage === "enter-email" ? (
        <div className="flex flex-col gap-4">
          <ContinueWithGoogleButton label="Sign up with Google" trackRegistration />
          <div className="flex items-center gap-3 text-2xs uppercase tracking-wider text-text-quaternary">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={onSubmitEmail} className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  autoComplete="given-name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Ada"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  autoComplete="family-name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Lovelace"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium">Plan</legend>
              <p className="text-xs text-text-tertiary">
                Elevate includes payroll, hiring, expenses, compensation, and reviews. Trial is 14
                days and leaves those locked.
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="planKey"
                  value="elevate"
                  checked={planKey === "elevate"}
                  onChange={() => setPlanKey("elevate")}
                />
                Elevate
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="planKey"
                  value="trial"
                  checked={planKey === "trial"}
                  onChange={() => setPlanKey("trial")}
                />
                Trial, 14 days
              </label>
            </fieldset>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
              />
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <Button type="submit" disabled={requestToken.isPending}>
              {requestToken.isPending ? "Signing up..." : "Sign up"}
            </Button>
            <p className="text-xs text-text-tertiary">
              Already have an account?{" "}
              <a href="/login" className="text-text-brand underline-offset-4 hover:underline">
                Sign in
              </a>
              .
            </p>
          </form>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-border bg-surface-subtle p-4 text-sm">
            <p className="font-medium">Verify your email</p>
            <p className="mt-1 text-text-secondary">
              We sent a link and a 6-digit code to <span className="font-medium">{email}</span>.
              Click the link, or type the code below.
            </p>
            <p className="mt-2 text-xs text-text-tertiary">
              Dev: open the shore terminal, the magic link + code are logged there.
            </p>
          </div>
          <form onSubmit={onSubmitOtp} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="otp">6-digit code</Label>
              <Input
                id="otp"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                autoFocus
              />
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <Button type="submit" disabled={verify.isPending || complete.isPending}>
              {verify.isPending || complete.isPending ? "Verifying…" : "Verify code"}
            </Button>
            <button
              type="button"
              className="text-xs text-text-tertiary underline-offset-4 hover:text-text-secondary hover:underline"
              onClick={() => {
                setStage("enter-email");
                setOtpCode("");
                setError(null);
              }}
            >
              Use a different email
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
